/**
 * Viewport — owns the canvas, renderer, scene, camera, and controls.
 *
 * Backend policy: three.js WebGPURenderer everywhere. When our capability
 * probe (or renderer init failure) rules out WebGPU, the same renderer runs
 * its WebGL 2 backend (`forceWebGL`) — one scene graph, one material system,
 * both paths. Post-processed GTAO ambient occlusion is attempted on both
 * backends and silently dropped if the pass can't build (older WebKit).
 *
 * Rendering is on-demand: nothing draws until something is dirty (camera
 * moved, geometry changed, resize). CAD idles at 0% GPU — matters on
 * MacBooks/iPads.
 */

import * as THREE from 'three/webgpu';
import {
  Discard,
  float,
  Fn,
  mrt,
  output,
  pass,
  positionWorld,
  transformedNormalView,
  uniform,
} from 'three/tsl';
import { ao } from 'three/addons/tsl/display/GTAONode.js';

import type { EvaluationReport } from '@core/timeline/evaluator';
import type { MeshData } from '@core/geometry/types';
import { deformedPositions } from './stressDeform';
import { CADControls } from './cadControls';
import type { GraphicsCapabilities } from './capabilities';
import {
  BODY_MATERIAL,
  CURVATURE_MATERIAL,
  curvatureColors,
  ensureNormals,
  meshDataToObject,
  SELECTED_MATERIAL,
  SKETCH_MATERIAL,
  STRESS_MATERIAL,
  stressRamp,
} from './meshView';
import { uvToWorld } from '@core/geometry/sketch';
import type { FeatureId } from '@core/timeline/types';
import {
  appearanceKey,
  bodyMaterialFor,
  configureBodyMaterials,
  type BodyAppearance,
} from './bodyMaterials';

export type StandardView =
  | 'iso'
  | 'front'
  | 'back'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom';

/** Orbit angles per named view (Z-up: theta around Z, phi from the pole). */
const STANDARD_VIEWS: Record<StandardView, { theta: number; phi: number }> = {
  iso: { theta: -Math.PI / 3, phi: Math.PI / 3 },
  front: { theta: -Math.PI / 2, phi: Math.PI / 2 }, // camera on −Y
  back: { theta: Math.PI / 2, phi: Math.PI / 2 },
  right: { theta: 0, phi: Math.PI / 2 }, // camera on +X
  left: { theta: Math.PI, phi: Math.PI / 2 },
  top: { theta: -Math.PI / 2, phi: 0.03 },
  bottom: { theta: -Math.PI / 2, phi: Math.PI - 0.03 },
};

export interface BodyHit {
  featureId: FeatureId;
  point: THREE.Vector3;
  /** Face normal in world space. */
  normal: THREE.Vector3;
  /** B-rep face under the hit (exact kernels only). */
  face?: {
    index: number;
    kind: 'planar' | 'cylindrical' | 'other';
    center: [number, number, number];
    /** Plane normal or cylinder axis direction. */
    dir: [number, number, number];
    axisPoint: [number, number, number];
    radius: number;
  };
}

export class Viewport {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: CADControls;
  /** 'webgpu' | 'webgl2' — what we actually initialized. */
  readonly backend: 'webgpu' | 'webgl2';
  aoEnabled = false;
  /** Curvature display (fairing check) — rebuild bodies after toggling. */
  curvatureOn = false;
  private selectedIds: readonly FeatureId[] = [];

  /** The element receiving pointer events (for tools' coordinate math). */
  get element(): HTMLElement {
    return this.host;
  }

  private renderer: THREE.WebGPURenderer;
  private postProcessing: THREE.PostProcessing | null = null;
  private readonly bodies = new THREE.Group();
  private readonly reference = new THREE.Group(); // grid, axes, shadow catcher
  /** Tool scratch space (sketch rubber bands, previews). Never raycast. */
  readonly overlay = new THREE.Group();
  /** Interactive widgets (scale handles etc.) — own their raycasting. */
  readonly gizmos = new THREE.Group();
  private needsRender = true;
  private rendering = false;
  private disposed = false;
  /** Captures own the canvas exclusively — the tick loop must not race them. */
  private capturing = false;
  private readonly resizeObserver: ResizeObserver;

  private constructor(
    private readonly host: HTMLElement,
    renderer: THREE.WebGPURenderer,
    backend: 'webgpu' | 'webgl2',
  ) {
    this.renderer = renderer;
    this.backend = backend;

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10_000);
    this.controls = new CADControls(this.camera, host);
    this.controls.onChange = () => this.requestRender();
    this.controls.onRequestFit = () => this.fitAll();
    this.controls.onDoublePress = (x, y) => this.focusUnderCursor(x, y);

    this.scene.background = new THREE.Color(0x1a1d23);
    this.scene.add(this.bodies, this.reference, this.overlay, this.gizmos);
    this.buildEnvironment();
    this.tryEnableReflections();
    this.tryEnableAO();

    this.host.appendChild(renderer.domElement);
    renderer.domElement.style.cssText = 'position:absolute;inset:0;display:block;';

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(host);
    this.resize();
    requestAnimationFrame(this.tick);
  }

  static async create(host: HTMLElement, gfx: GraphicsCapabilities): Promise<Viewport> {
    const make = async (forceWebGL: boolean): Promise<THREE.WebGPURenderer> => {
      const renderer = new THREE.WebGPURenderer({ antialias: true, forceWebGL });
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.1;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      await renderer.init();
      return renderer;
    };

    // ?gl=1 forces the WebGL 2 backend — for testing the Safari fallback
    // path and as a user escape hatch for broken WebGPU drivers.
    const forceWebGL =
      gfx.backend !== 'webgpu' ||
      new URLSearchParams(location.search).get('gl') === '1';
    try {
      return new Viewport(host, await make(forceWebGL), forceWebGL ? 'webgl2' : 'webgpu');
    } catch (err) {
      if (forceWebGL) throw err;
      // WebGPU probe passed but the renderer failed (seen on some WebKit
      // builds) — retry once on the WebGL 2 backend.
      console.warn('WebGPU init failed; falling back to WebGL 2', err);
      return new Viewport(host, await make(true), 'webgl2');
    }
  }

  // --- scene furniture -------------------------------------------------------

  private buildEnvironment(): void {
    const hemi = new THREE.HemisphereLight(0xdfe8f5, 0x3a3f47, 1.1);
    hemi.position.set(0, 0, 1);

    const key = new THREE.DirectionalLight(0xffffff, 2.4);
    key.position.set(180, -240, 320);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = key.shadow.camera.bottom = -280;
    key.shadow.camera.right = key.shadow.camera.top = 280;
    key.shadow.camera.near = 50;
    key.shadow.camera.far = 1200;
    key.shadow.bias = -0.0004;

    const fill = new THREE.DirectionalLight(0xbfd0e8, 0.5);
    fill.position.set(-220, 180, 140);

    const grid = new THREE.GridHelper(500, 50, 0x3b414b, 0x262a31);
    ensureNormals(grid.geometry);
    grid.rotation.x = Math.PI / 2; // three grids are XZ; NovaCAD is Z-up on XY

    const axes = new THREE.Group();
    for (const [dir, color] of [
      [new THREE.Vector3(1, 0, 0), 0xc4504e], // X east, red
      [new THREE.Vector3(0, 1, 0), 0x59a869], // Y north, green
    ] as const) {
      const geo = ensureNormals(
        new THREE.BufferGeometry().setFromPoints([
          dir.clone().multiplyScalar(-250),
          dir.clone().multiplyScalar(250),
        ]),
      );
      axes.add(new THREE.Line(geo, new THREE.LineBasicMaterial({ color })));
    }
    axes.position.z = 0.02; // above grid lines, below parts

    // No ground shadow-catcher: three r170's shadow-only materials
    // (ShadowMaterial / ShadowNodeMaterial) generate invalid WGSL on the
    // WebGPU backend when the scene has non-shadow lights ("shadowMask *
    // null"), and one invalid pipeline drops the ENTIRE frame. Bodies still
    // cast/receive shadows on each other. Revisit on the next three bump.
    this.reference.add(hemi, key, fill, grid, axes);
  }

  /**
   * Reflection environment: a tiny procedural gradient equirect (bright sky,
   * mid horizon, dark ground). Without SOMETHING to reflect, metallic
   * materials render near-black under analytic lights; with it, brushed
   * aluminum reads as aluminum. Failure caps displayed metalness instead.
   */
  private tryEnableReflections(): void {
    try {
      const w = 64, h = 32;
      const data = new Uint8Array(w * h * 4);
      for (let y = 0; y < h; y++) {
        // t: 0 at the bottom row (ground) → 1 at the top (sky).
        const t = y / (h - 1);
        const sky = [205, 215, 232], mid = [120, 126, 138], gnd = [42, 46, 54];
        const mix = (a: number, b: number, k: number): number => a + (b - a) * k;
        const c =
          t > 0.5
            ? [0, 1, 2].map((i) => mix(mid[i]!, sky[i]!, (t - 0.5) * 2))
            : [0, 1, 2].map((i) => mix(gnd[i]!, mid[i]!, t * 2));
        for (let x = 0; x < w; x++) {
          const o = (y * w + x) * 4;
          data[o] = c[0]!; data[o + 1] = c[1]!; data[o + 2] = c[2]!; data[o + 3] = 255;
        }
      }
      const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      tex.magFilter = THREE.LinearFilter;
      tex.minFilter = THREE.LinearFilter;
      tex.needsUpdate = true;
      this.scene.environment = tex;
      this.scene.environmentIntensity = 0.5;
      configureBodyMaterials({ environment: true });
    } catch (err) {
      console.warn('Environment reflections unavailable; capping metalness', err);
      configureBodyMaterials({ environment: false });
    }
  }

  /** GTAO via TSL post-processing; harmless no-op where unsupported. */
  private tryEnableAO(): void {
    try {
      const scenePass = pass(this.scene, this.camera);
      scenePass.setMRT(mrt({ output, normal: transformedNormalView }));
      const color = scenePass.getTextureNode('output');
      const normal = scenePass.getTextureNode('normal');
      const depth = scenePass.getTextureNode('depth');
      const aoPass = ao(depth, normal, this.camera);
      const post = new THREE.PostProcessing(this.renderer);
      post.outputNode = aoPass.getTextureNode().mul(color);
      this.postProcessing = post;
      this.aoEnabled = true;
    } catch (err) {
      console.warn('GTAO unavailable on this backend; continuing without AO', err);
      this.postProcessing = null;
      this.aoEnabled = false;
    }
  }

  // --- geometry --------------------------------------------------------------

  /** Body currently painted with FEA stress colors (selection must not clobber). */
  private stressedId: FeatureId | null = null;

  /** Highlight the selected features' bodies (single id, list, or none). */
  setSelected(ids: FeatureId | readonly FeatureId[] | null): void {
    this.selectedIds = ids === null ? [] : Array.isArray(ids) ? ids : [ids as FeatureId];
    for (const child of this.bodies.children) {
      const fid = child.userData.featureId as FeatureId;
      if (fid === this.stressedId) continue; // keep the stress ramp visible
      const isSel = this.selectedIds.includes(fid);
      child.traverse((node) => {
        if (node instanceof THREE.Mesh && !this.curvatureOn) {
          const base = (node.userData.baseMaterial as THREE.Material) ?? BODY_MATERIAL;
          node.material = this.clipped(isSel ? SELECTED_MATERIAL : base);
        }
      });
    }
    this.requestRender();
  }

  /**
   * Paint FEA von Mises vertex colors on one body (values normalized against
   * `max`), optionally displacing vertices by `displacement × scale` for the
   * exaggerated deformed view (edge overlay hides — it can't follow).
   * Returns false when the body isn't displayed or the vertex count doesn't
   * match the current mesh (stale analysis).
   */
  showStress(
    featureId: FeatureId,
    vertexVonMises: Float32Array,
    max: number,
    displacement?: Float32Array,
    scale = 0,
  ): boolean {
    // One stress view at a time — restore any previously painted body.
    if (this.stressedId !== null && this.stressedId !== featureId) this.clearStressView();
    const entry = this.bodyCache.get(featureId);
    if (!entry) return false;
    let ok = false;
    entry.obj.traverse((node) => {
      if (node instanceof THREE.LineSegments) {
        node.visible = false; // B-rep edges don't deform with the mesh
        return;
      }
      if (!(node instanceof THREE.Mesh)) return;
      const pos = node.geometry.getAttribute('position') as THREE.BufferAttribute;
      if (pos.count !== vertexVonMises.length) return;
      const colors = new Float32Array(pos.count * 3);
      const inv = max > 0 ? 1 / max : 0;
      for (let i = 0; i < pos.count; i++) {
        const [r, g, b] = stressRamp(vertexVonMises[i]! * inv);
        colors[i * 3] = r; colors[i * 3 + 1] = g; colors[i * 3 + 2] = b;
      }
      node.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      if (displacement && displacement.length === pos.count * 3 && scale > 0) {
        // NEVER write into pos.array — it WRAPS the evaluator's shared
        // Float32Array (exports, snapping, and re-solves all read it).
        // Deform into a private copy and swap the attribute.
        const orig =
          (node.userData.origPositions as Float32Array | undefined) ??
          (pos.array as Float32Array);
        node.userData.origPositions = orig;
        const deformed = deformedPositions(orig, displacement, scale);
        node.geometry.setAttribute('position', new THREE.BufferAttribute(deformed, 3));
        node.geometry.computeBoundingSphere();
      } else if (node.userData.origPositions) {
        // Repaint WITHOUT deformation: drop a previous run's deformed copy.
        node.geometry.setAttribute(
          'position',
          new THREE.BufferAttribute(node.userData.origPositions as Float32Array, 3),
        );
        node.geometry.computeBoundingSphere();
        delete node.userData.origPositions;
      }
      node.material = this.clipped(STRESS_MATERIAL);
      ok = true;
    });
    if (ok) this.stressedId = featureId;
    this.requestRender();
    return ok;
  }

  /** Restore the stressed body's material, positions, and edges. */
  clearStressView(): void {
    if (this.stressedId === null) return;
    const id = this.stressedId;
    const entry = this.bodyCache.get(id);
    this.stressedId = null;
    const isSel = this.selectedIds.includes(id);
    // Curvature display legitimately coexists with a stress overlay; showStress
    // overwrote its per-vertex colors + material, so restoring must re-apply
    // curvature (matching buildFeatureObject) rather than fall to the base
    // material — otherwise the body stays stuck showing the stress ramp.
    const mesh = entry?.obj.userData.mesh as MeshData | undefined;
    entry?.obj.traverse((node) => {
      if (node instanceof THREE.LineSegments) {
        node.visible = true;
        return;
      }
      if (!(node instanceof THREE.Mesh)) return;
      const orig = node.userData.origPositions as Float32Array | undefined;
      if (orig) {
        // Re-wrap the untouched shared array (the deformed copy is dropped).
        node.geometry.setAttribute('position', new THREE.BufferAttribute(orig, 3));
        node.geometry.computeBoundingSphere();
        delete node.userData.origPositions;
      }
      if (this.curvatureOn) {
        if (mesh) {
          node.geometry.setAttribute('color', new THREE.BufferAttribute(curvatureColors(mesh), 3));
        }
        node.material = this.clipped(CURVATURE_MATERIAL);
      } else {
        const base = (node.userData.baseMaterial as THREE.Material) ?? BODY_MATERIAL;
        node.material = this.clipped(isSel ? SELECTED_MATERIAL : base);
      }
    });
    this.requestRender();
  }

  /**
   * Section-aware material. The renderer's built-in clippingPlanes proved
   * unreliable on the WebGPU backend (r170: per-object clipping contexts go
   * stale / apply inconsistently), so the section is cut in the SHADER: a
   * TSL fragment discard against a world-space plane held in uniforms.
   * Uniform updates move the plane live — no pipeline rebuilds.
   */
  private clipped(base: THREE.Material): THREE.Material {
    if (!this.sectionOn) return base;
    // Transparent materials keep their opacity through the cutaway node.
    const opacity = base.transparent ? base.opacity : 1;
    const cutaway = Fn(() => {
      Discard(
        positionWorld.dot(this.sectionNormal).add(this.sectionConstant).lessThan(0),
      );
      return float(opacity);
    });
    if (base instanceof THREE.LineBasicMaterial) {
      const m = new THREE.LineBasicNodeMaterial();
      m.color.copy(base.color);
      m.opacityNode = cutaway();
      return m;
    }
    const src = base as THREE.MeshStandardMaterial;
    const m = new THREE.MeshStandardNodeMaterial();
    m.color.copy(src.color);
    m.metalness = src.metalness;
    m.roughness = src.roughness;
    m.emissive.copy(src.emissive);
    m.emissiveIntensity = src.emissiveIntensity;
    m.map = src.map;
    m.roughnessMap = src.roughnessMap;
    m.transparent = src.transparent;
    m.depthWrite = src.depthWrite;
    m.side = src.side;
    m.vertexColors = src.vertexColors; // curvature display composes with section
    m.opacityNode = cutaway();
    return m;
  }

  /** Per-feature display cache: rebuild only what actually changed. */
  private readonly bodyCache = new Map<
    FeatureId,
    { result: unknown; obj: THREE.Object3D; epoch: number; app: string }
  >();
  private displayEpoch = 0;
  private lastCurvatureOn = false;
  private lastSectionOn = false;
  /** Effective material/color per visible body (resolved by the app layer). */
  private appearances: ReadonlyMap<FeatureId, BodyAppearance> = new Map();

  /**
   * Sync displayed geometry with an evaluation report. Unchanged results
   * (same reference — the evaluator caches them) keep their existing scene
   * objects; only changed/new/removed features rebuild. Display-mode flips
   * (section, curvature) bump an epoch that invalidates everything; a body's
   * appearance key changing rebuilds just that body (geometry arrays are
   * reused — no kernel work).
   */
  updateBodies(
    report: EvaluationReport,
    appearances?: ReadonlyMap<FeatureId, BodyAppearance>,
  ): void {
    if (appearances) this.appearances = appearances;
    if (this.curvatureOn !== this.lastCurvatureOn || this.sectionOn !== this.lastSectionOn) {
      this.lastCurvatureOn = this.curvatureOn;
      this.lastSectionOn = this.sectionOn;
      this.displayEpoch++;
    }
    const live = new Set<FeatureId>();
    for (const [featureId, result] of report.results) {
      if (report.consumed.has(featureId)) continue; // absorbed by a boolean
      const appKey = appearanceKey(this.appearances.get(featureId));
      const cached = this.bodyCache.get(featureId);
      if (
        cached && cached.result === result &&
        cached.epoch === this.displayEpoch && cached.app === appKey
      ) {
        live.add(featureId);
        continue;
      }
      if (cached) this.disposeBodyObject(cached.obj);
      if (featureId === this.stressedId) this.stressedId = null; // rebuilt → colors gone
      this.bodyCache.delete(featureId);
      const built = this.buildFeatureObject(featureId, result);
      if (built) {
        this.bodies.add(built);
        this.bodyCache.set(featureId, {
          result, obj: built, epoch: this.displayEpoch, app: appKey,
        });
        live.add(featureId);
      }
    }
    // Features gone from the report (deleted, suppressed, now-consumed).
    for (const [id, c] of this.bodyCache) {
      if (!live.has(id)) {
        this.disposeBodyObject(c.obj);
        if (id === this.stressedId) this.stressedId = null;
        this.bodyCache.delete(id);
      }
    }
    this.requestRender();
  }

  /**
   * Remove a body's display object AND free its GPU geometry — rebuilds
   * (edits, appearance changes) would otherwise leak buffers for the life
   * of the tab. Materials are NOT disposed: bodies share library instances.
   */
  private disposeBodyObject(obj: THREE.Object3D): void {
    obj.removeFromParent();
    obj.traverse((n) => {
      if (n instanceof THREE.Mesh || n instanceof THREE.Line) {
        (n.geometry as THREE.BufferGeometry).dispose();
      }
    });
  }

  /** One feature's display object (body mesh+edges, or sketch curves). */
  private buildFeatureObject(
    featureId: FeatureId,
    result: EvaluationReport['results'] extends ReadonlyMap<FeatureId, infer R> ? R : never,
  ): THREE.Object3D | null {
    {
      if (result.kind === 'body' && result.mesh.positions.length > 0) {
        const obj = meshDataToObject(
          result.mesh,
          this.selectedIds.includes(featureId),
          bodyMaterialFor(this.appearances.get(featureId)),
        );
        obj.userData.featureId = featureId;
        obj.userData.mesh = result.mesh; // edge picking reads groups/midpoints
        if (this.curvatureOn) {
          obj.traverse((n) => {
            if (n instanceof THREE.Mesh) {
              n.geometry.setAttribute(
                'color',
                new THREE.BufferAttribute(curvatureColors(result.mesh), 3),
              );
              n.material = CURVATURE_MATERIAL;
            }
          });
        }
        if (this.sectionOn) {
          obj.traverse((n) => {
            if (n instanceof THREE.Mesh || n instanceof THREE.Line) {
              n.material = this.clipped(n.material as THREE.Material);
            }
          });
        }
        return obj;
      }
      if (result.kind === 'sketch') {
        const group = new THREE.Group();
        group.userData.featureId = featureId;
        for (const region of result.profiles) {
          for (const loop of [region.outer, ...region.holes]) {
            const pts = loop.map(
              (p) => new THREE.Vector3(...uvToWorld(region.planeToWorld, p)),
            );
            if (pts.length > 1) pts.push(pts[0]!.clone()); // close the loop
            // (WebGPURenderer has no LineLoop support — use a closed Line.)
            const geo = ensureNormals(new THREE.BufferGeometry().setFromPoints(pts));
            const line = new THREE.Line(geo, this.clipped(SKETCH_MATERIAL) as THREE.LineBasicMaterial);
            line.userData.featureId = featureId;
            line.raycast = () => undefined; // sketch curves don't block face picks
            group.add(line);
          }
        }
        return group.children.length > 0 ? group : null;
      }
    }
    return null;
  }

  /** Pick the topmost body face under a client-space point. */
  /**
   * Project a world point to client (screen) pixels. Returns null when the
   * point is behind the camera (marquee ignores those bodies).
   */
  projectToClient(x: number, y: number, z: number): [number, number] | null {
    const v = new THREE.Vector3(x, y, z);
    // In camera space, a point in front has negative Z; behind → skip.
    const cam = v.clone().applyMatrix4(this.camera.matrixWorldInverse);
    if (cam.z >= 0) return null;
    v.project(this.camera);
    const rect = this.host.getBoundingClientRect();
    return [
      (v.x * 0.5 + 0.5) * rect.width + rect.left,
      (-v.y * 0.5 + 0.5) * rect.height + rect.top,
    ];
  }

  raycastBodies(clientX: number, clientY: number): BodyHit | null {
    const rect = this.host.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.camera);
    for (const hit of ray.intersectObjects(this.bodies.children, true)) {
      if (!hit.face) continue;
      let node: THREE.Object3D | null = hit.object;
      while (node && node.userData.featureId === undefined) node = node.parent;
      if (!node) continue;
      const normal = hit.face.normal
        .clone()
        .transformDirection(hit.object.matrixWorld)
        .normalize();
      const result: BodyHit = {
        featureId: node.userData.featureId as FeatureId,
        point: hit.point,
        normal,
      };
      // B-rep face identity under the hit (triangle → face index → frame).
      const mesh = node.userData.mesh as
        | { triFaceIndex?: Uint32Array; faceKinds?: Uint8Array; faceData?: Float32Array }
        | undefined;
      if (
        mesh?.triFaceIndex && mesh.faceKinds && mesh.faceData &&
        hit.faceIndex !== undefined && hit.faceIndex !== null
      ) {
        const fi = mesh.triFaceIndex[hit.faceIndex];
        if (fi !== undefined && fi < mesh.faceKinds.length) {
          const k = mesh.faceKinds[fi]!;
          const d = mesh.faceData;
          result.face = {
            index: fi,
            kind: k === 1 ? 'planar' : k === 2 ? 'cylindrical' : 'other',
            center: [d[fi * 10]!, d[fi * 10 + 1]!, d[fi * 10 + 2]!],
            dir: [d[fi * 10 + 3]!, d[fi * 10 + 4]!, d[fi * 10 + 5]!],
            axisPoint: [d[fi * 10 + 6]!, d[fi * 10 + 7]!, d[fi * 10 + 8]!],
            radius: d[fi * 10 + 9]!,
          };
        }
      }
      return result;
    }
    return null;
  }

  /**
   * Pick a B-rep EDGE of one body: nearest display segment under the cursor
   * → owning edge index → that edge's curve midpoint (its stable identity).
   */
  raycastEdge(
    clientX: number,
    clientY: number,
    featureId: FeatureId,
  ): { midpoint: [number, number, number]; edgeIndex: number } | null {
    // Stress view hides the (undeformed) edge overlay — picking against
    // invisible lines that no longer match the displayed shape is a trap.
    if (featureId === this.stressedId) return null;
    const group = this.bodies.children.find((c) => c.userData.featureId === featureId);
    const mesh = group?.userData.mesh as
      | { edgeGroups?: Uint32Array; edgeMidpoints?: Float32Array }
      | undefined;
    if (!group || !mesh?.edgeGroups || !mesh.edgeMidpoints) return null;
    let lines: THREE.LineSegments | null = null;
    group.traverse((n) => {
      if (n instanceof THREE.LineSegments) lines = n;
    });
    if (!lines) return null;

    const rect = this.host.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    // Pixel-ish pick tolerance scaled to camera distance.
    const camDist = this.camera.position.distanceTo(this.controls.getTarget());
    ray.params.Line.threshold = camDist * 0.012;
    ray.setFromCamera(ndc, this.camera);
    const hits = ray.intersectObject(lines, false);
    const hit = hits[0] as (THREE.Intersection & { index?: number }) | undefined;
    if (!hit || hit.index === undefined) return null;
    const segIdx = Math.floor(hit.index / 2);
    const edgeIndex = mesh.edgeGroups[segIdx];
    if (edgeIndex === undefined) return null;
    const m = mesh.edgeMidpoints;
    return {
      edgeIndex,
      midpoint: [m[edgeIndex * 3]!, m[edgeIndex * 3 + 1]!, m[edgeIndex * 3 + 2]!],
    };
  }

  fitAll(): void {
    const box = new THREE.Box3().setFromObject(this.bodies);
    if (box.isEmpty()) box.set(new THREE.Vector3(-80, -80, 0), new THREE.Vector3(80, 80, 80));
    this.controls.fitBox(box);
  }

  /** Model extent along a world axis (section-view slider range). */
  bodiesRange(axis: 'X' | 'Y' | 'Z'): { min: number; max: number } {
    const box = new THREE.Box3().setFromObject(this.bodies);
    if (box.isEmpty()) return { min: -100, max: 100 };
    const k = axis.toLowerCase() as 'x' | 'y' | 'z';
    return { min: box.min[k], max: box.max[k] };
  }

  /** Section state: shader-side plane uniforms (see clipped()). */
  private sectionOn = false;
  private readonly sectionNormal = uniform(new THREE.Vector3(0, 0, -1));
  private readonly sectionConstant = uniform(0);

  /**
   * Section view: cut the MODEL (bodies, edges, sketches) at a world-axis
   * plane; grid, axes, gizmos and overlays stay intact. `flip` keeps the
   * other side. null = off.
   *
   * Returns true when the on/off state changed — the caller must then
   * rebuild displayed bodies (updateBodies) so objects swap between plain
   * and cutaway materials. Plane moves while on are pure uniform updates.
   */
  setSection(cfg: { axis: 'X' | 'Y' | 'Z'; offset: number; flip: boolean } | null): boolean {
    if (!cfg) {
      const was = this.sectionOn;
      this.sectionOn = false;
      this.requestRender();
      return was;
    }
    const dir = { X: [1, 0, 0], Y: [0, 1, 0], Z: [0, 0, 1] }[cfg.axis];
    // Keep fragments where n·p + constant ≥ 0. Default keeps the side BELOW
    // the offset (normal −axis, constant +offset); flip inverts.
    const s = cfg.flip ? 1 : -1;
    this.sectionNormal.value.set(dir[0]! * s, dir[1]! * s, dir[2]! * s);
    this.sectionConstant.value = -s * cfg.offset;
    const structural = !this.sectionOn;
    this.sectionOn = true;
    this.requestRender();
    return structural;
  }

  /** Double-click: re-center the orbit target on the model point under cursor. */
  private focusUnderCursor(clientX: number, clientY: number): void {
    const hit = this.raycastBodies(clientX, clientY);
    if (hit) this.controls.setTarget(hit.point);
    else this.fitAll();
  }

  // --- frame loop ------------------------------------------------------------

  requestRender(): void {
    this.needsRender = true;
  }

  /**
   * Capture the current view as a PNG data URL (AI render input, thumbnails).
   * WebGPU canvases can't be read after presenting, so render explicitly and
   * copy into a 2D canvas within the same task.
   */
  /**
   * Capture from a named standard view WITHOUT disturbing the user's camera:
   * save orbit state → orient + fit → render + grab → restore → re-render.
   */
  async captureFrom(
    view: StandardView | undefined,
    maxSize = 1024,
    format: 'png' | 'jpeg' = 'png',
  ): Promise<string> {
    if (!view) return this.capture(maxSize, format);
    const angles = STANDARD_VIEWS[view];
    if (!angles) return this.capture(maxSize, format);
    const saved = this.controls.getState();
    try {
      this.controls.setOrientation(angles.theta, angles.phi);
      this.fitAll(); // frame the model from the new direction
      return await this.capture(maxSize, format);
    } finally {
      this.controls.setState(saved);
      this.requestRender();
    }
  }

  async capture(maxSize = 1024, format: 'png' | 'jpeg' = 'png'): Promise<string> {
    this.capturing = true;
    try {
      return await this.captureInner(maxSize, format);
    } finally {
      this.capturing = false;
    }
  }

  private async captureInner(maxSize: number, format: 'png' | 'jpeg'): Promise<string> {
    const render = async (): Promise<void> => {
      if (this.postProcessing) await this.postProcessing.renderAsync();
      else await this.renderer.renderAsync(this.scene, this.camera);
    };
    await render();
    // WebGPU presents at frame boundaries — without yielding, drawImage sees
    // the PREVIOUS presented frame (multi-angle captures in one task would
    // all return the same stale image). Yield so this frame presents, then
    // render once more so the canvas' CURRENT texture also holds our view —
    // covering both drawImage source semantics.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await render();
    const src = this.renderer.domElement;
    const scale = Math.min(1, maxSize / Math.max(src.width, src.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(src.width * scale);
    canvas.height = Math.round(src.height * scale);
    const ctx = canvas.getContext('2d')!;
    if (format === 'jpeg') {
      // JPEG has no alpha — fill with the scene background first.
      ctx.fillStyle = '#1a1d23';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
    return format === 'jpeg'
      ? canvas.toDataURL('image/jpeg', 0.85)
      : canvas.toDataURL('image/png');
  }

  private resize(): void {
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.requestRender();
  }

  private tick = (): void => {
    if (this.disposed) return;
    requestAnimationFrame(this.tick);
    if (!this.needsRender || this.rendering || this.capturing) return;
    this.needsRender = false;
    this.rendering = true;
    const done = (): void => {
      this.rendering = false;
    };
    if (this.postProcessing) {
      this.postProcessing.renderAsync().then(done, (err: unknown) => {
        // AO pass failed at first real render — drop to plain rendering.
        console.warn('Post-processing render failed; disabling AO', err);
        this.postProcessing = null;
        this.aoEnabled = false;
        this.requestRender();
        done();
      });
    } else {
      this.renderer.renderAsync(this.scene, this.camera).then(done, (err: unknown) => {
        console.error(err);
        done();
      });
    }
  };

  dispose(): void {
    this.disposed = true;
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
